import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { useSupabase } from "@/auth/SupabaseProvider";

import { apiFetch } from "./client";
import type {
  ClearOrphanDraftsResponse,
  OrphanDraftsResponse,
} from "./types";

/**
 * Orphan-drafts recovery queries — backs the "Clear drafts" banner on
 * JobDetail.
 *
 * The banner is a recovery affordance for `tools_draft` rows authored
 * by a terminal job that were never cleared (expired, post-rollup
 * warn-log, or curator-failed-mid-author paths). The orchestrator
 * computes the `canClear` predicate from `(terminal-status AND non-
 * empty intersection of draft_authorship ∩ live tools_draft)` — the
 * SPA reads `canClear` directly rather than re-deriving it client-side,
 * so the predicate lives in one place.
 *
 * See `services/orchestrator/src/routes/clear-orphan-drafts.ts` and
 * issue #158 for the design rationale.
 */

/**
 * Fetch the orphan-drafts probe for a job. Runs whenever `jobId` is
 * truthy — non-terminal jobs return `canClear: false` and the banner
 * renders nothing. Keeping the fetch unconditional (rather than gating
 * by status) is cheap (one DB query + one Sheet read) and keeps the
 * `canClear` predicate centralised in the orchestrator.
 */
export function useOrphanDrafts(jobId: string | undefined) {
  const { client } = useSupabase();
  return useQuery({
    queryKey: ["orphan-drafts", jobId],
    enabled: Boolean(jobId),
    queryFn: ({ signal }) =>
      apiFetch<OrphanDraftsResponse>(
        client,
        `/api/jobs/${jobId}/orphan-drafts`,
        { signal },
      ),
  });
}

/**
 * Clear orphan drafts for a job. Invalidates the per-job detail query
 * (so the banner re-evaluates after the clear) and the orphan-drafts
 * query itself (so a fresh GET reflects the post-clear `canClear:
 * false` state).
 *
 * Mirrors `useRetryValidator` in [`./jobs.ts`](./jobs.ts) — same shape,
 * same invalidation pattern.
 */
export function useClearOrphanDrafts() {
  const { client } = useSupabase();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) =>
      apiFetch<ClearOrphanDraftsResponse>(
        client,
        `/api/jobs/${jobId}/clear-orphan-drafts`,
        { method: "POST" },
      ),
    onSuccess: (_data, jobId) => {
      void queryClient.invalidateQueries({ queryKey: ["jobs", jobId] });
      void queryClient.invalidateQueries({ queryKey: ["orphan-drafts", jobId] });
    },
  });
}
